/**
 * Meta value sidecar — theme capture layer.
 *
 * Responsibilities (all read config from window.MetaSidecarConfig, rendered by
 * snippets/meta-sidecar.liquid; nothing runs unless the theme setting is on):
 *
 *  1. Klaviyo popup listener: `klaviyoForms` window events → POST email step
 *     to /api/events/lead, SMS step to /api/events/sms-optin.
 *  2. Cart attribute stamping: writes _fbp/_fbc (+ landing/UTM context) into
 *     cart attributes via /cart/update.js so the orders/create webhook can
 *     attach browser identity to QualifiedSample. Only stamps carts that have
 *     items — it never creates empty carts.
 *  3. Trade form identity hint: on trade pages, captures the email
 *     entered into the Shopify Forms block at submit time and posts it with
 *     fbp/fbc so the customers webhook can join them later.
 *
 * This file never calls fbq() and never writes Meta's cookies — Elevar owns
 * the browser pixel. Everything here is CAPI-side enrichment only.
 */
(function () {
  'use strict';

  var cfg = window.MetaSidecarConfig;
  if (!cfg || !cfg.endpoint || !cfg.store) return;

  var debug = function () {
    if (cfg.debug && window.console) console.log.apply(console, ['[meta-sidecar]'].concat([].slice.call(arguments)));
  };

  /* ---------- consent -------------------------------------------------- */

  // US-default policy: absence of a consent signal allows; an explicit "no"
  // from Shopify's Customer Privacy API denies. The server applies LDU flags.
  function consentState() {
    try {
      var api = window.Shopify && window.Shopify.customerPrivacy;
      if (api && typeof api.currentVisitorConsent === 'function') {
        var c = api.currentVisitorConsent();
        if (c && c.marketing === 'no') return false;
        if (c && c.marketing === 'yes') return true;
      }
    } catch (err) { /* treat as no signal */ }
    return null; // no signal
  }

  /* ---------- identity helpers ----------------------------------------- */

  function getCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }

  // fbc: prefer the pixel's _fbc cookie; else synthesize from a live fbclid
  // (Meta's documented format) and remember it for later steps this session.
  function getFbc() {
    var cookie = getCookie('_fbc');
    if (cookie) return cookie;
    try {
      var fbclid = new URLSearchParams(location.search).get('fbclid');
      if (fbclid) {
        var synthesized = 'fb.1.' + Date.now() + '.' + fbclid;
        localStorage.setItem('_msc_fbc', synthesized);
        return synthesized;
      }
      return localStorage.getItem('_msc_fbc');
    } catch (err) {
      return null;
    }
  }

  function identity() {
    return { fbp: getCookie('_fbp'), fbc: getFbc() };
  }

  /* ---------- transport ------------------------------------------------- */

  function send(path, payload) {
    var consent = consentState();
    if (consent === false) {
      debug('blocked by consent', path);
      return;
    }
    var id = identity();
    var body = JSON.stringify(Object.assign({}, payload, {
      fbp: id.fbp,
      fbc: id.fbc,
      url: location.href,
      consent: consent === null ? undefined : consent,
    }));
    var url = cfg.endpoint.replace(/\/$/, '') + path + '?store=' + encodeURIComponent(cfg.store);
    debug('send', url, body);
    try {
      // sendBeacon survives popup-close/navigation races.
      if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))) return;
    } catch (err) { /* fall through */ }
    fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: body, keepalive: true }).catch(function () {});
  }

  /* ---------- 1. Klaviyo popup ------------------------------------------ */

  function findValue(obj, matcher, validator, depth) {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (var key in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
      var val = obj[key];
      if (matcher.test(key) && typeof val === 'string' && validator(val)) return val;
      if (val && typeof val === 'object') {
        var nested = findValue(val, matcher, validator, (depth || 0) + 1);
        if (nested) return nested;
      }
    }
    return null;
  }

  var isEmail = function (v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()); };
  var isPhone = function (v) { return v.replace(/\D/g, '').length >= 7; };

  function guardOnce(kind, value) {
    try {
      var key = '_msc_sent_' + kind;
      var sent = JSON.parse(localStorage.getItem(key) || '[]');
      var token = btoa(unescape(encodeURIComponent(value.trim().toLowerCase()))).slice(0, 24);
      if (sent.indexOf(token) !== -1) return false;
      sent.push(token);
      localStorage.setItem(key, JSON.stringify(sent.slice(-10)));
      return true;
    } catch (err) {
      return true; // storage unavailable — rely on server-side dedupe
    }
  }

  var lastSeenEmail = null;

  function handleKlaviyoEvent(e) {
    var d = e && e.detail;
    if (!d || (d.type !== 'stepSubmit' && d.type !== 'submit')) return;
    debug('klaviyoForms', d.type, d.formId, d.metaData);

    var email = findValue(d.metaData || d, /email/i, isEmail, 0);
    var phone = findValue(d.metaData || d, /phone|sms/i, isPhone, 0);

    if (email) lastSeenEmail = email;
    if (email && guardOnce('lead', email)) {
      send('/api/events/lead', { email: email, formId: d.formId });
    }
    if (phone && guardOnce('sms', phone)) {
      send('/api/events/sms-optin', { phone: phone, email: lastSeenEmail || undefined, formId: d.formId });
    }
  }

  if (cfg.trackKlaviyo) {
    window.addEventListener('klaviyoForms', handleKlaviyoEvent);
  }

  /* ---------- 2. cart attribute stamping -------------------------------- */

  function utmContext() {
    var out = {};
    try {
      var params = new URLSearchParams(location.search);
      ['utm_source', 'utm_medium', 'utm_campaign'].forEach(function (k) {
        var v = params.get(k);
        if (v) {
          out['_sc_' + k] = v;
          sessionStorage.setItem('_msc_' + k, v);
        } else {
          var stored = sessionStorage.getItem('_msc_' + k);
          if (stored) out['_sc_' + k] = stored;
        }
      });
    } catch (err) { /* ignore */ }
    return out;
  }

  var stampInFlight = false;

  function stampCart() {
    if (!cfg.stampCart || stampInFlight) return;
    if (consentState() === false) return;
    var id = identity();
    if (!id.fbp && !id.fbc) return;

    var desired = Object.assign({}, utmContext());
    if (id.fbp) desired._fbp = id.fbp;
    if (id.fbc) desired._fbc = id.fbc;
    if (consentState() === true) desired._sc_consent = 'yes';
    var marker = JSON.stringify(desired);
    try {
      if (localStorage.getItem('_msc_stamped') === marker) return;
    } catch (err) { /* proceed */ }

    stampInFlight = true;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || cart.item_count === 0) { stampInFlight = false; return; } // never create empty carts
        var stale = Object.keys(desired).some(function (k) {
          return (cart.attributes || {})[k] !== desired[k];
        });
        if (!stale) {
          try { localStorage.setItem('_msc_stamped', marker); } catch (err) { /* ignore */ }
          stampInFlight = false;
          return;
        }
        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ attributes: desired }),
        }).then(function () {
          debug('cart stamped', desired);
          try { localStorage.setItem('_msc_stamped', marker); } catch (err) { /* ignore */ }
          stampInFlight = false;
        });
      })
      .catch(function () { stampInFlight = false; });
  }

  // Stamp when a cart exists on load, and after any add-to-cart (covers the
  // UpCart drawer and theme AJAX adds, which all hit /cart/add*).
  if (cfg.stampCart) {
    var origFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var target = String(args[0] && args[0].url ? args[0].url : args[0] || '');
      var result = origFetch.apply(this, args);
      if (/\/cart\/(add|change|update)/.test(target)) {
        result.then(function () { setTimeout(stampCart, 250); }).catch(function () {});
      }
      return result;
    };
    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (/\/cart\/(add|change|update)/.test(String(url))) {
        this.addEventListener('load', function () { setTimeout(stampCart, 250); });
      }
      return origOpen.apply(this, arguments);
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { setTimeout(stampCart, 500); });
    } else {
      setTimeout(stampCart, 500);
    }
  }

  /* ---------- 3. trade form identity hint -------------------------------- */

  if (cfg.tradeHint && cfg.isTradePage) {
    document.addEventListener(
      'submit',
      function (e) {
        try {
          var form = e.target;
          if (!form || !form.querySelector) return;
          var input = form.querySelector('input[type="email"], input[name*="email" i]');
          var email = input && input.value;
          if (email && isEmail(email)) {
            send('/api/events/identity-hint', { email: email });
          }
        } catch (err) { /* never break the merchant's form */ }
      },
      true
    );
  }
})();
